// Electrical pricing engine
//
// The legacy Electrical flow (in RequestWizard.jsx) did NOT compute a running
// estimate — it always returned null. As part of the modular migration we
// bring Electrical up to parity with Handyman/Painting/Plumbing by producing
// a real running estimate from the selected services + follow-up quantities.
//
// Base prices live on each service in electricalData.js; here we sum them
// and apply the correct quantity multiplier per follow-up question.

import { ELECTRICAL_SERVICE_PRICES } from './electricalData';

// Quantity buckets → numeric multipliers.
const OUTLET_MULT = { '1': 1, '2-3': 2.5, '4-6': 5, '7+': 7 };
const LIGHTING_MULT = { '1': 1, '2-3': 2.5, '4-6': 5, '7+': 7 };
const RECESSED_MULT = { '1-4': 1, '5-8': 1.75, '9-12': 2.75, '12+': 3.5 };
const SMART_MULT = { '1': 1, '2-3': 2.5, '4-6': 5, '7+': 7 };
const EV_MULT = { level_1: 1, level_2: 2 };

// Which follow-up controls the quantity for each service value.
const OUTLET_SERVICES = ['install_new_outlet', 'replace_outlet', 'gfci_outlet', 'usb_outlet'];
const LIGHTING_SERVICES = ['light_fixture', 'ceiling_fan', 'chandelier', 'pendant_light', 'vanity_light', 'exterior_light'];
const SMART_SERVICES = ['smart_thermostat', 'smart_switch', 'smart_outlet', 'smart_doorbell', 'smart_lock', 'smart_camera'];

/**
 * Look up the multiplier for a given service based on the follow-up answer.
 * Defaults to 1 when the follow-up hasn't been answered yet.
 */
const getMultiplier = (serviceValue, answers) => {
  if (OUTLET_SERVICES.includes(serviceValue)) {
    return OUTLET_MULT[answers.outlet_quantity] || 1;
  }
  if (LIGHTING_SERVICES.includes(serviceValue)) {
    return LIGHTING_MULT[answers.lighting_quantity] || 1;
  }
  if (serviceValue === 'recessed_lighting') {
    return RECESSED_MULT[answers.recessed_count] || 1;
  }
  if (SMART_SERVICES.includes(serviceValue)) {
    return SMART_MULT[answers.smart_device_quantity] || 1;
  }
  if (serviceValue === 'ev_charger') {
    return EV_MULT[answers.ev_charger_type] || 1;
  }
  // panel_upgrade + other services: no quantity multiplier
  return 1;
};

/**
 * Calculate the running estimate for the Electrical flow.
 *
 * @param {Array<string>} selectedServices  answers.electrical_services
 * @param {Object}        answers           all wizard answers
 * @returns {{ low: number, high: number, hasValidPrice: boolean }}
 */
export const calculateElectricalEstimate = (selectedServices = [], answers = {}) => {
  let low = 0;
  let high = 0;

  const services = Array.isArray(selectedServices) ? selectedServices : (answers.electrical_services || []);
  services.forEach((serviceValue) => {
    const price = ELECTRICAL_SERVICE_PRICES[serviceValue];
    if (!price) return;
    const multiplier = getMultiplier(serviceValue, answers);
    low += Math.round(price.low * multiplier);
    high += Math.round(price.high * multiplier);
  });

  return {
    low,
    high,
    hasValidPrice: low > 0 || high > 0
  };
};

export default calculateElectricalEstimate;
